import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import dayjs from 'dayjs';
import { Snackbar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import API from '../api';
import { POPUP_DURATION_SUCCESS, POPUP_DURATION_ERROR } from './popUpStyles';
import { RedOutlinedButton } from './Buttons';
import { DecisionDialog } from './DecisionDialog';
import { CalendarDialog } from '../layouts/calendar/CalendarDialog';
import { FullScreenDialog } from '../layouts/calendar/FullScreenDialog';
import { NewEventDialog } from '../layouts/calendar/NewEventDialog';
import { HttpErrorHandler } from './ErrorHandler';

const localizer = momentLocalizer(moment);

/**
 * @param {boolean} isUser
 * @param {date} requestDay
 * @returns Calendar Page UI
 */
export const CalendarUI = ({ isUser, requestDay, ...props }) => {
  const [snackbarText, setSnackbarText] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [currentEvent, setCurrentEvent] = useState('');
  const [eventLists, setEventLists] = useState([]);

  // Calendar functions
  const [openCalendarEvent, setOpenCalendarEvent] = useState(false);
  const [openDeleteEventDialog, setDeleteEventDialog] = useState(false);
  const [openModifyEventDialog, setModifyEventDialog] = useState(false);
  const [openNewEventDialog, setNewEventDialog] = useState(false);
  const [hasAddessDetails, setHasAddressDetails] = useState(false);

  // modify event
  const [eventTitle, setEventTitle] = useState('');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventEntity, setEventEntity] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventStatus, setEventStatus] = useState('');
  const [eventStreet, setEventStreet] = useState('');
  const [eventCap, setEventCap] = useState('');
  const [eventCity, setEventCity] = useState('');
  const [eventProvince, setEventProvince] = useState('');
  const [eventCountry, setEventCountry] = useState('');

  // new event
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventStartDate, setNewEventStartDate] = useState('');
  const [newEventEndDate, setNewEventEndDate] = useState('');
  const [newEventStartTime, setNewEventStartTime] = useState('');
  const [newEventEndTime, setNewEventEndTime] = useState('');
  const [newEventDescription, setNewEventDescription] = useState('');
  const [newEventEntity, setNewEventEntity] = useState('');
  const [newEventType, setNewEventType] = useState('');
  const [newEventStatus, setNewEventStatus] = useState('');
  const [newEventStreet, setNewEventStreet] = useState('');
  const [newEventCap, setNewEventCap] = useState('');
  const [newEventCity, setNewEventCity] = useState('');
  const [newEventProvince, setNewEventProvince] = useState('');
  const [newEventCountry, setNewEventCountry] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);

  // new event address, type & status lists
  const [cityList, setCityList] = useState([]);
  const [provinceList, setProvinceList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [entityList, setEntityList] = useState([]);
  const [eventTypeList, setEventTypeList] = useState([]);
  const [eventStatusList, setEventStatusList] = useState([]);

  // open calendar event dialog
  const handleCalendarOpen = (event) => {
    setCurrentEvent(event);
    setIsAllDay(Boolean(event.all_day));
    setOpenCalendarEvent(true);
  };

  // close calendar event dialog
  const handleCalendarClose = () => {
    setOpenCalendarEvent(false);
  };

  // open delete calendar event dialog
  const handleDeleteEventOpen = () => {
    setDeleteEventDialog(true);
  };

  // close delete calendar event dialog
  const handleDeleteEventClose = () => {
    setDeleteEventDialog(false);
  };

  // Delete an event request
  const onDeleteEvent = () => {
    API.delete(`calendars/admin/${currentEvent.id}`)
      .then((response) => {
        handleDeleteEventClose();
        handleCalendarClose();
        setSnackbarText("L'evento è stato cancellato!");
        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
        }, POPUP_DURATION_SUCCESS);
        getEventLists();
      })
      .catch((error) => {
        HttpErrorHandler(error, setSnackbarText);
        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
        }, POPUP_DURATION_ERROR);
      });
  };

  // open modify calendar event dialog
  const handleModifyEventOpen = () => {
    getAddressApi();
    setModifyEventDialog(true);
    handleCalendarClose();
  };

  // close modify calendar event dialog
  const handleModifyEventClose = () => {
    setModifyEventDialog(false);
  };

  // refersh modify event dialog after modify operation
  const refreshModifyEventDialog = () => {
    setEventTitle('');
    setEventStartDate('');
    setEventEndDate('');
    setEventStartTime('');
    setEventEndTime('');
    setEventDescription('');
    setEventEntity('');
    setEventType('');
    setEventStatus('');
    setEventStreet('');
    setEventCap('');
    setEventCity('');
    setEventProvince('');
    setEventCountry('');
    setIsAllDay(false);
  };

  // modify event request
  const onModifyEvent = () => {
    const modifiedEvent = {
      title: eventTitle.length !== 0 ? eventTitle : currentEvent.title,
      start_date:
        eventStartDate.length !== 0
          ? dayjs(eventStartDate).format('YYYY/MM/DD')
          : dayjs(currentEvent.start_date).format('YYYY/MM/DD'),
      start_time: isAllDay
        ? null
        : eventStartTime.length !== 0
        ? eventStartTime
        : dayjs(currentEvent.start_time, 'HH:mm').format('HH:mm'),
      end_date:
        eventEndDate.length !== 0
          ? dayjs(eventEndDate).format('YYYY/MM/DD')
          : dayjs(currentEvent.end_date).format('YYYY/MM/DD'),
      end_time: isAllDay
        ? null
        : eventEndTime.length !== 0
        ? eventEndTime
        : dayjs(currentEvent.end_time, 'HH:mm').format('HH:mm'),
      all_day: isAllDay,
      description: eventDescription.length !== 0 ? eventDescription : currentEvent.description,
      entity_id: eventEntity.length !== 0 ? eventEntity.id : currentEvent.entity_id,
      z_event_type_id: eventType.length !== 0 ? eventType.id : currentEvent.z_event_type_id,
      z_event_status_id: eventStatus.length !== 0 ? eventStatus.id : currentEvent.z_event_status_id,
      street: eventStreet.length !== 0 ? eventStreet : currentEvent.street,
      cap: eventCap.length !== 0 ? eventCap : currentEvent.cap,
      z_city_id: eventCity.length !== 0 ? eventCity.id : currentEvent.z_city_id,
      z_province_id: eventProvince.length !== 0 ? eventProvince.id : currentEvent.z_province_id,
      z_country_id: eventCountry.length !== 0 ? eventCountry.id : currentEvent.z_country_id,
    };

    API.put(`/calendars/admin/${currentEvent.id}`, modifiedEvent)
      .then((response) => {
        const updatedEvent = response.data;
        setSnackbarText(updatedEvent.message);
        refreshModifyEventDialog();
        getEventLists();

        handleModifyEventClose();
        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
        }, POPUP_DURATION_SUCCESS);
      })
      .catch((error) => {
        handleModifyEventClose();
        HttpErrorHandler(error, setSnackbarText);
        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
        }, POPUP_DURATION_ERROR);
      });
  };

  // open new event dialog
  const handleNewEventDialogOpen = (start, end) => {
    getAddressApi();
    setNewEventStartDate(start);
    setNewEventStartTime(dayjs(start).format('HH:mm'));
    setNewEventEndDate(end);
    setNewEventEndTime(dayjs(end).format('HH:mm'));
    setNewEventDialog(true);
  };

  // close new event dialog
  const handleNewEventDialogClose = () => {
    setNewEventDialog(false);
  };

  // refresh new event dialog after making creation request
  const refreshNewEventDialog = () => {
    setNewEventTitle('');
    setNewEventStartDate('');
    setNewEventEndDate('');
    setNewEventStartTime('');
    setNewEventEndTime('');
    setNewEventDescription('');
    setNewEventEntity('');
    setNewEventType('');
    setNewEventStatus('');
    setNewEventStreet('');
    setNewEventCap('');
    setNewEventCity('');
    setNewEventProvince('');
    setNewEventCountry('');
    setIsAllDay(false);
  };

  // Create event request
  const createEvent = () => {
    const newEvent = {
      title: newEventTitle,
      start_date: dayjs(newEventStartDate).format('YYYY/MM/DD'),
      start_time: isAllDay ? null : newEventStartTime,
      end_date: dayjs(newEventEndDate).format('YYYY/MM/DD'),
      end_time: isAllDay ? null : newEventEndTime,
      all_day: isAllDay,
      description: newEventDescription,
      entity_id: newEventEntity.length !== 0 ? newEventEntity.id : '',
      z_event_type_id: newEventType.length !== 0 ? newEventType.id : '',
      z_event_status_id: newEventStatus.length !== 0 ? newEventStatus.id : '',
      street: newEventStreet,
      cap: newEventCap,
      z_city_id: newEventCity.length !== 0 ? newEventCity.id : '',
      z_province_id: newEventProvince.length !== 0 ? newEventProvince.id : '',
      z_country_id: newEventCountry.length !== 0 ? newEventCountry.id : '',
    };

    API.post('/calendars/admin', newEvent)
      .then((response) => {
        setSnackbarText("L'evento è stato salvato!");
        getEventLists();
        refreshNewEventDialog();
        handleNewEventDialogClose();
        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
        }, POPUP_DURATION_SUCCESS);
      })
      .catch((error) => {
        handleNewEventDialogClose();
        HttpErrorHandler(error, setSnackbarText);
        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
        }, POPUP_DURATION_ERROR);
      });
  };

  // Get api lists:
  // -> City, Province, Country, Entity ('Federazioni'), Event Type & Event Status
  const getAddressApi = () => {
    if (
      cityList.length === 0 ||
      provinceList.length === 0 ||
      countryList.length === 0 ||
      entityList.length === 0 ||
      eventTypeList.length === 0 ||
      eventStatusList.length === 0
    ) {
      setHasAddressDetails(false);

      axios
        .all([
          API.get(`/cities`),
          API.get(`/provinces`),
          API.get(`/countries`),
          API.get(`/entities`),
          API.get(`/eventtype`),
          API.get(`/calendarstatus`),
        ])
        .then(
          axios.spread(
            (
              cityResponse,
              provinceResponse,
              countryResponse,
              entityResponse,
              eventTypeResponse,
              eventStatusResponse
            ) => {
              setCityList(cityResponse.data);
              setProvinceList(provinceResponse.data);
              setCountryList(countryResponse.data);
              setEntityList(entityResponse.data.data);
              setEventTypeList(eventTypeResponse.data.event_types);
              setEventStatusList(eventStatusResponse.data.event_statuses);
              setHasAddressDetails(true);
            }
          )
        )
        .catch((error) => {
          HttpErrorHandler(error, setSnackbarText);
          setOpenSnackbar(true);
          setHasAddressDetails(true);

          setTimeout(() => {
            setOpenSnackbar(false);
          }, POPUP_DURATION_ERROR);
        });
    } else {
      setHasAddressDetails(true);
    }
  };

  // snackbar close event handler
  const onSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  // snackbar icon with event handler
  const snackbarAction = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={onSnackBarClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  // Get Event list by roles: user or admin
  const getEventLists = () => {
    API.get(isUser ? '/calendars/user' : '/calendars/admin')
      .then((response) => {
        const newEvent = [];
        response.data.data.forEach((event) => {
          newEvent.push({
            start: new Date(`${event.start_date} ${event.start_time}`),
            end: new Date(`${event.end_date} ${event.end_time}`),
            address: `${event.street}, ${event.cap}, ${event.city_name} (${event.province_name}), ${event.region_name}, ${event.country_name}`,
            allDay: event.all_day,
            ...event,
          });
        });
        setEventLists(newEvent);
      })
      .catch((error) => {
        HttpErrorHandler(error, setSnackbarText);
        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
        }, POPUP_DURATION_ERROR);
      });
  };

  // Initially event loading requests
  useEffect(() => {
    getEventLists();
  }, []);

  return (
    <div {...props}>
      {/** Generate Calendar Page UI */}
      <Calendar
        messages={{
          next: 'Successivo',
          previous: 'Precedente',
          today: 'Oggi',
          month: 'Mese',
          week: 'Settimana',
          year: 'Anno',
          day: 'Giorno',
          allDay: 'Tutto il giorno',
          agenda: 'Agenda',
          date: 'Data',
          time: 'Ora',
          event: 'Evento',
        }}
        localizer={localizer}
        events={eventLists}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100vh' }}
        eventPropGetter={(event, start, end, isSelected) => {
          const backgroundColor = isSelected ? '#007F54' : '#a9e0cf';
          const color = isSelected ? '#FFF' : '#000';

          return {
            style: { backgroundColor, color, outlineColor: '#007F54' },
          };
        }}
        onSelectEvent={(event) => handleCalendarOpen(event)}
        selectable
        onSelectSlot={({ start, end }) => isUser || handleNewEventDialogOpen(start, end - 1)}
        defaultView={requestDay !== null ? Views.DAY : Views.MONTH}
        defaultDate={requestDay !== null ? requestDay : null}
      />

      {/** Single event visualization on pop-up dialog UI  */}
      <CalendarDialog
        openDialog={openCalendarEvent}
        handleClose={handleCalendarClose}
        event={currentEvent}
        handleDeleteEvent={handleDeleteEventOpen}
        handleModifyEvent={handleModifyEventOpen}
        isUser={isUser}
      />

      {/** Confirmation dialog UI before deleting an event */}
      <DecisionDialog
        openDialog={openDeleteEventDialog}
        dialogTitle="Eliminazione evento"
        dialogDescription="Sei sicuro di voler eliminare l'evento selezionato?"
        closeOperation={handleDeleteEventClose}
        requestOperation={onDeleteEvent}
        requestText={'Elimina'}
        closeText={'Chiudi'}
        RequestButton={RedOutlinedButton}
      />

      {/** Modify event dialog UI */}
      <FullScreenDialog
        openDialog={openModifyEventDialog}
        closeDialogEvent={handleModifyEventClose}
        event={currentEvent}
        onSave={onModifyEvent}
        eventTitle={setEventTitle}
        eventStartDate={setEventStartDate}
        eventEndDate={setEventEndDate}
        eventStartTime={setEventStartTime}
        eventEndTime={setEventEndTime}
        setIsAllDay={setIsAllDay}
        isAllDay={isAllDay}
        eventDescription={setEventDescription}
        setEventEntity={setEventEntity}
        setEventType={setEventType}
        setEventStatus={setEventStatus}
        setEventStreet={setEventStreet}
        setEventCap={setEventCap}
        setEventCity={setEventCity}
        setEventProvince={setEventProvince}
        setEventCountry={setEventCountry}
        entityList={entityList}
        eventTypeList={eventTypeList}
        eventStatusList={eventStatusList}
        cityList={cityList}
        provinceList={provinceList}
        countryList={countryList}
        hasAddressDetails={hasAddessDetails}
      />

      {/** New Event dialog UI */}
      <NewEventDialog
        openDialog={openNewEventDialog}
        onClose={handleNewEventDialogClose}
        onCreateEvent={createEvent}
        start={newEventStartDate}
        end={newEventEndDate}
        setEventTitle={setNewEventTitle}
        setEventStartDate={setNewEventStartDate}
        setEventStartTime={setNewEventStartTime}
        setEventEndDate={setNewEventEndDate}
        setEventEndTime={setNewEventEndTime}
        setAllDay={setIsAllDay}
        isAllDay={isAllDay}
        setEventDescription={setNewEventDescription}
        setEventEntity={setNewEventEntity}
        setEventType={setNewEventType}
        setEventStatus={setNewEventStatus}
        setEventStreet={setNewEventStreet}
        setEventCap={setNewEventCap}
        setEventCity={setNewEventCity}
        setEventProvince={setNewEventProvince}
        setEventCountry={setNewEventCountry}
        entityList={entityList}
        eventTypeList={eventTypeList}
        eventStatusList={eventStatusList}
        cityList={cityList}
        provinceList={provinceList}
        countryList={countryList}
        hasAddressDetails={hasAddessDetails}
      />

      {/** Snackbar UI */}
      <Snackbar
        open={openSnackbar}
        onClose={onSnackBarClose}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={snackbarAction}
        message={snackbarText}
      />
    </div>
  );
};

// Declartion of Calendar UI component parameter types
CalendarUI.propTypes = {
  isUser: PropTypes.bool,
  requestDay: PropTypes.object,
};
